import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Sparkles, 
  Search, 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Building2,
  Users,
  TrendingUp,
  Target,
  Leaf,
  Shield,
  Calendar,
  ClipboardCheck
} from 'lucide-react';

export default function MarchesPublicsNettoyagePage() {
  return (
    <>
      <SEO 
        title="Marchés Publics Nettoyage : Appels d'Offres Propreté et Entretien"
        description="Comment trouver des marchés publics de nettoyage et propreté. Guide complet : appels d'offres entretien des locaux, analyse DCE propreté, certifications requises. Conseils pour entreprises de nettoyage."
        path="/marches-publics-nettoyage"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        <Breadcrumb 
          items={[
            { label: 'Marchés publics nettoyage' }
          ]} 
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-50 via-emerald-50 to-white py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Secteur Nettoyage & Propreté
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Marchés Publics Nettoyage : Comment Trouver des Appels d'Offres en Propreté
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
                Le secteur de la propreté représente un marché considérable dans la commande publique. 
                Découvrez comment identifier les opportunités et remporter des contrats d'entretien 
                de locaux, de nettoyage industriel et de services de propreté.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700">
                  <Search className="w-5 h-5 mr-2" />
                  Trouver des marchés nettoyage
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: '15 Md€', label: 'marché de la propreté/an', icon: TrendingUp },
                { value: '550 000', label: 'salariés dans le secteur', icon: Users },
                { value: '85%', label: 'des marchés aux PME', icon: Target },
                { value: '+8%', label: 'croissance annuelle', icon: Leaf }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <stat.icon className="w-8 h-8 text-teal-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            
            {/* Section 1 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-teal-600" />
                Quels types de marchés publics en nettoyage ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Les <strong>marchés publics de nettoyage</strong> couvrent une grande variété de prestations, 
                  allant de l'entretien courant des locaux à des prestations spécialisées. 
                  Voici les principales catégories :
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                {[
                  { title: 'Entretien courant des locaux', desc: 'Bureaux, écoles, mairies, salles des fêtes, bibliothèques', icon: Building2 },
                  { title: 'Nettoyage industriel', desc: 'Ateliers municipaux, cuisines centrales, déchetteries', icon: Shield },
                  { title: 'Nettoyage de vitres', desc: 'Façades vitrées, vérandas, vitrines de bâtiments publics', icon: Sparkles },
                  { title: 'Remise en état', desc: 'Après travaux, après sinistre, décapage et traitement des sols', icon: ClipboardCheck }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-teal-50 rounded-xl p-5 border border-teal-100">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-teal-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-4 mt-6 rounded-r-lg">
                <p className="text-teal-800">
                  <strong>Bon à savoir :</strong> Les marchés de nettoyage sont souvent pluriannuels 
                  (3 à 4 ans) avec reconduction tacite. Surveillez les dates de renouvellement pour 
                  anticiper les consultations.
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Search className="w-8 h-8 text-teal-600" />
                Où trouver les appels d'offres nettoyage ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Les marchés de propreté sont publiés par de nombreux acheteurs publics. 
                  Voici les principaux donneurs d'ordre à surveiller :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { 
                    name: 'Collectivités territoriales', 
                    desc: 'Mairies, communautés de communes, conseils départementaux',
                    detail: 'Entretien des bâtiments publics, écoles, équipements sportifs'
                  },
                  { 
                    name: 'Établissements de santé', 
                    desc: 'Hôpitaux, cliniques, EHPAD, centres médico-sociaux',
                    detail: 'Nettoyage hospitalier (bionettoyage), désinfection'
                  },
                  { 
                    name: 'Établissements scolaires', 
                    desc: 'Écoles, collèges, lycées, universités, CROUS',
                    detail: 'Entretien des salles de classe, cantines, internats'
                  },
                  { 
                    name: 'Bailleurs sociaux', 
                    desc: 'Offices HLM, SEM, organismes de logement social',
                    detail: 'Nettoyage des parties communes, halls, escaliers'
                  },
                  { 
                    name: 'Administrations d\'État', 
                    desc: 'Préfectures, tribunaux, ministères, services déconcentrés',
                    detail: 'Entretien des bureaux et locaux administratifs'
                  }
                ].map((source, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-teal-300 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900">{source.name}</h3>
                      <p className="text-slate-600 text-sm">{source.desc}</p>
                      <span className="text-xs text-teal-600 font-medium">{source.detail}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-teal-600 text-white rounded-2xl p-6 mt-8">
                <h3 className="text-xl font-bold mb-3">Automatisez votre veille nettoyage</h3>
                <p className="text-teal-100 mb-4">
                  Avec notre <Link to="/veille-marches-publics" className="underline font-medium text-white">veille marchés publics</Link>, 
                  recevez automatiquement les appels d'offres de nettoyage correspondant à votre zone géographique 
                  et à vos spécialités.
                </p>
                <Link to="/">
                  <Button variant="secondary" className="bg-white text-teal-600 hover:bg-teal-50">
                    Configurer ma veille propreté
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-teal-600" />
                Comment analyser un DCE de nettoyage ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Les DCE de marchés de propreté contiennent des éléments spécifiques à bien identifier. 
                  Voici les points clés à analyser :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { step: '1', title: 'Surfaces et fréquences', desc: 'Métrage total, répartition par type de local, fréquence d\'intervention (quotidien, hebdo, mensuel)' },
                  { step: '2', title: 'Prestations attendues', desc: 'Détail des tâches : sols, sanitaires, vitres, mobilier, espaces verts éventuels' },
                  { step: '3', title: 'Horaires d\'intervention', desc: 'Créneaux imposés (souvent hors heures d\'ouverture), contraintes d\'accès' },
                  { step: '4', title: 'Produits et matériel', desc: 'Fourniture des produits incluse ou non, exigences éco-responsables, matériel mis à disposition' },
                  { step: '5', title: 'Clause de reprise du personnel', desc: 'Application de l\'article 7 de la CCN Propreté (reprise des salariés en place)' }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1 bg-slate-50 rounded-xl p-4">
                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-slate-600 mt-6">
                Notre <Link to="/analyse-dce" className="text-teal-600 font-medium hover:underline">outil d'analyse DCE par IA</Link> extrait 
                automatiquement les surfaces, fréquences et prestations clés pour vous aider à chiffrer rapidement.
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-teal-600" />
                Les erreurs fréquentes des entreprises de nettoyage
              </h2>
              
              <div className="space-y-4">
                {[
                  { 
                    error: 'Sous-estimer le temps d\'intervention',
                    solution: 'Calculez précisément le temps nécessaire par type de prestation. Un devis trop serré génère des difficultés d\'exécution.'
                  },
                  { 
                    error: 'Ignorer la clause de reprise du personnel',
                    solution: 'La CCN Propreté impose la reprise des salariés affectés au marché. Intégrez leurs conditions dans votre offre.'
                  },
                  { 
                    error: 'Négliger les certifications',
                    solution: 'Qualipropre, ISO 14001, Ecolabel... Ces certifications sont de plus en plus demandées et valorisées.'
                  },
                  { 
                    error: 'Oublier de visiter les locaux',
                    solution: 'La visite permet d\'évaluer l\'état des sols, les contraintes d\'accès et de détecter des prestations non décrites.'
                  },
                  { 
                    error: 'Proposer un prix anormalement bas',
                    solution: 'Les acheteurs connaissent les prix du marché. Un prix trop bas sera rejeté ou entraînera des difficultés d\'exécution.'
                  }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold text-sm">✗</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.error}</h3>
                        <div className="flex items-start gap-2 mt-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                          <p className="text-slate-600 text-sm">{item.solution}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-teal-600" />
                Comment gagner du temps dans la veille propreté ?
              </h2>
              
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Les clés d'une veille efficace</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Mots-clés à surveiller</h4>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Nettoyage des locaux</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Entretien ménager</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Propreté</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Bionettoyage</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Lavage de vitres</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Codes CPV propreté</h4>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 90910000 - Services de nettoyage</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 90911200 - Nettoyage de bâtiments</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 90911300 - Nettoyage de vitres</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 90919200 - Nettoyage de bureaux</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-teal-600 to-emerald-600 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Trouvez vos prochains marchés de nettoyage
            </h2>
            <p className="text-teal-100 mb-8 max-w-xl mx-auto">
              Recevez gratuitement 3 recommandations de marchés publics 
              correspondant à vos activités de propreté.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-teal-600 hover:bg-teal-50">
                <Sparkles className="w-5 h-5 mr-2" />
                Commencer ma veille propreté gratuite
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="nettoyage" />

        <Footer />
      </div>
    </>
  );
}
